import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { MoonpayCommerceDeposit } from '@heliofi/deposit-react'
import { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useDepositConfig } from '../api/useDepositConfig'

/**
 * Floor for the deposit slot, so the dialog keeps its size while MoonPay boots.
 *
 * Sized just above the height MoonPay's own panel settles at when it first paints, which means the
 * modal is the same size before, during and after the wait -- no shift in either direction. The
 * widget grows past this on its own once someone picks a method inside it; that growth follows a
 * click, so it reads as a response rather than as the box moving on its own.
 */
const RESERVED = 'min-h-[360px]'

/**
 * Height at which we call the widget painted.
 *
 * There is no ready callback on `MoonpayCommerceDeposit`, so its own layout is the signal. It
 * occupies a few empty pixels from the moment it mounts; anything under this is that empty shell.
 */
const PAINTED_PX = 120

/** MoonPay can fail without rendering anything. Stop spinning eventually rather than spin forever. */
const MAX_WAIT_MS = 15000

/**
 * Watches the wrapper and reports once the third-party widget has laid something real out inside it.
 */
const useWidgetPainted = (enabled: boolean) => {
    const ref = useRef<HTMLDivElement>(null)
    const [painted, setPainted] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!enabled || !el) return

        const observer = new ResizeObserver(() => {
            if (el.offsetHeight >= PAINTED_PX) {
                setPainted(true)
                observer.disconnect()
            }
        })
        observer.observe(el)

        const timer = window.setTimeout(() => setPainted(true), MAX_WAIT_MS)
        return () => {
            observer.disconnect()
            window.clearTimeout(timer)
        }
    }, [enabled])

    return { ref, painted }
}

/**
 * The wait used to look like a bug.
 *
 * Two things happen back to back here: the customer token is fetched, then MoonPay's widget boots
 * its own iframe. The second one takes seconds and nothing used to cover it -- the slot rendered at
 * whatever height the empty widget happened to have, so the dialog collapsed to a stub with the
 * tabs floating over nothing. People read that as broken and closed it before it finished.
 *
 * So the slot holds its height throughout and says it is working. The widget is mounted the whole
 * time and merely faded, never unmounted or hidden with `display`, because delaying its mount would
 * add to the very wait this is covering.
 */
export const MoonpayDeposit = () => {
    const { data, isLoading, isError } = useDepositConfig()
    const { ref, painted } = useWidgetPainted(!!data)

    const waiting = !isError && (isLoading || !data || !painted)

    if (isError && !data)
        return (
            <div className='col-span-2 md:col-span-4 text-center py-8 text-red-500'>Failed to initialize deposit</div>
        )

    return (
        <div className={twMerge('relative col-span-2 md:col-span-4', RESERVED)}>
            {data && (
                <div
                    ref={ref}
                    className={twMerge('transition-opacity duration-200', waiting && 'opacity-0 pointer-events-none')}
                >
                    <MoonpayCommerceDeposit
                        config={{
                            depositCustomerToken: data.customerToken,
                            display: 'inline',
                            network: 'main',
                            themeMode: 'dark',
                        }}
                    />
                </div>
            )}

            {waiting && (
                <div className='absolute inset-0 flex flex-col items-center justify-center gap-4'>
                    <FontAwesomeIcon icon={faSpinner} spin className='text-3xl text-white/70' />
                    <p className='text-xs uppercase tracking-[0.2em] text-white/45'>Connecting to MoonPay</p>
                </div>
            )}
        </div>
    )
}
